import React from 'react';
import { X, Copy, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from './ui/button.jsx';
import { useToaster } from '../contexts/ToasterContext';
import { getSeverityInfo } from '../utils/integrationHelpers.js';

const SeverityBadge = ({ severity }) => {
  const severityInfo = getSeverityInfo(severity);
  return (
    <span className={`inline-flex shrink-0 items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityInfo.bg} ${severityInfo.text}`}>
      {severityInfo.label}
    </span>
  );
};

const CopyButton = ({ text, label = "Copy" }) => {
  const { showToast } = useToaster();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied to clipboard`, 'info');
    } catch (err) {
      console.error('Failed to copy text: ', err);
      showToast('Failed to copy to clipboard', 'error');
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleCopy}
      className="h-8 px-2 text-xs"
    >
      <Copy className="w-3 h-3 mr-1" />
      Copy
    </Button>
  );
};

const RemediationSidebar = ({ isOpen, onClose, finding }) => {
  // Prevent body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const getResourceField = (finding) => {
    return finding?.resource_arn || finding?.resource_id || finding?.resource_name || 'Unknown';
  };

  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved':
        return { 
          icon: <CheckCircle className="w-4 h-4 text-green-600" />,
          textColor: "text-green-600"
        };
      case 'in_progress':
        return { 
          icon: <Clock className="w-4 h-4 text-blue-600" />,
          textColor: "text-blue-600"
        };
      default:
        return { 
          icon: <AlertTriangle className="w-4 h-4 text-yellow-600" />,
          textColor: "text-yellow-600"
        };
    }
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num == null) return '0';
    return num.toLocaleString();
  };

  if (!isOpen || !finding) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white sticky top-0">
          <div className="flex items-center gap-3 flex-1">
            <SeverityBadge severity={finding.severity} />
            <h2 className="text-lg font-semibold text-gray-900 break-words">
              {finding.title}
            </h2>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 shrink-0 ml-2"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Finding Overview */}
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Resource</h3>
                <CopyButton 
                  text={getResourceField(finding)} 
                  label="Resource" 
                />
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-sm text-gray-900 font-mono break-all">
                  {getResourceField(finding)}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
              <div className="flex items-center gap-2">
                {getStatusInfo(finding.status).icon}
                <span className={`text-sm capitalize font-medium ${getStatusInfo(finding.status).textColor}`}>
                  {finding.status || 'Open'}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-600 leading-relaxed">
                  <ReactMarkdown 
                    components={{
                      h1: ({node, children, ...props}) => <h1 className="text-lg font-bold text-gray-900 mt-4 mb-2" {...props}>{children}</h1>,
                      h2: ({node, children, ...props}) => <h2 className="text-base font-semibold text-gray-900 mt-3 mb-2" {...props}>{children}</h2>,
                      h3: ({node, children, ...props}) => <h3 className="text-sm font-semibold text-gray-900 mt-2 mb-1" {...props}>{children}</h3>,
                      p: ({node, children, ...props}) => <p className="text-sm text-gray-600 mb-2 leading-relaxed" {...props}>{children}</p>,
                      ul: ({node, children, ...props}) => <ul className="text-sm text-gray-600 list-disc list-inside mb-2" {...props}>{children}</ul>,
                      ol: ({node, children, ...props}) => <ol className="text-sm text-gray-600 list-decimal list-inside mb-2" {...props}>{children}</ol>,
                      li: ({node, children, ...props}) => <li className="text-sm text-gray-600 mb-1" {...props}>{children}</li>,
                      strong: ({node, children, ...props}) => <strong className="font-semibold text-gray-700" {...props}>{children}</strong>,
                      code: ({node, inline, children, ...props}) => 
                        inline ? (
                          <code className="text-xs text-green-600 font-mono inline" {...props}>{children}</code>
                        ) : (
                          <code className="text-xs text-green-600 font-mono inline whitespace-nowrap" {...props}>{children}</code>
                        ),
                      pre: ({node, children, ...props}) => <span className="text-green-600 font-mono inline" {...props}>{children}</span>,
                    }}
                  >
                    {finding.description}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>

          {/* Remediation Plan */}
          {finding.remediation_plan && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Remediation Plan</h3>
                <CopyButton 
                  text={finding.remediation_plan} 
                  label="Plan" 
                />
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 prose prose-sm max-w-none">
                <ReactMarkdown 
                  components={{
                    h1: ({node, children, ...props}) => <h1 className="text-lg font-bold text-gray-900 mt-4 mb-2" {...props}>{children}</h1>,
                    h2: ({node, children, ...props}) => <h2 className="text-base font-semibold text-gray-900 mt-3 mb-2" {...props}>{children}</h2>,
                    h3: ({node, children, ...props}) => <h3 className="text-sm font-semibold text-gray-900 mt-2 mb-1" {...props}>{children}</h3>,
                    p: ({node, children, ...props}) => <p className="text-sm text-gray-700 mb-2 leading-relaxed" {...props}>{children}</p>,
                    ul: ({node, children, ...props}) => <ul className="text-sm text-gray-700 list-disc list-inside mb-2" {...props}>{children}</ul>,
                    ol: ({node, children, ...props}) => <ol className="text-sm text-gray-700 list-decimal list-inside mb-2" {...props}>{children}</ol>,
                    li: ({node, children, ...props}) => <li className="text-sm text-gray-700 mb-1" {...props}>{children}</li>,
                    strong: ({node, children, ...props}) => <strong className="font-semibold" {...props}>{children}</strong>,
                    code: ({node, inline, children, ...props}) => 
                      inline ? (
                        <code className="text-xs text-green-600 font-mono inline" {...props}>{children}</code>
                      ) : (
                        <code className="text-xs text-green-600 font-mono inline whitespace-nowrap" {...props}>{children}</code>
                      ),
                    pre: ({node, children, ...props}) => <span className="text-green-600 font-mono inline" {...props}>{children}</span>,
                    blockquote: ({node, children, ...props}) => <blockquote className="border-l-4 border-blue-400 pl-4 italic text-gray-700 mb-2" {...props}>{children}</blockquote>,
                  }}
                >
                  {finding.remediation_plan}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* CLI Commands */}
          {finding.remediation_cli && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">CLI Commands</h3>
                <CopyButton 
                  text={finding.remediation_cli} 
                  label="Command" 
                />
              </div>
              <div className="p-4 bg-gray-900 rounded-lg">
                <code className="text-sm text-green-400 font-mono whitespace-pre-wrap break-all">
                  {finding.remediation_cli}
                </code>
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="space-y-6">
            {finding.tags && finding.tags.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {finding.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm hover:shadow-md transition-shadow"
                    >
                      {tag.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {finding.compliances && finding.compliances.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">Compliances</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-gray-700 leading-relaxed space-y-2">
                  {finding.compliances.map((compliance, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold mt-0.5 flex-shrink-0">•</span>
                      <span>{compliance}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RemediationSidebar;


